import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Settings, DollarSign, Calendar, Shield, UserCheck, Sparkles, Save, Info } from 'lucide-react';
import { toast } from 'sonner';
import { aiTerms } from '@/lib/api';

export default function AITermsConfig({ hotelId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [terms, setTerms] = useState(null);

  useEffect(() => {
    loadTerms();
  }, [hotelId]);

  const loadTerms = async () => {
    try {
      const { data } = await aiTerms.get(hotelId);
      setTerms(data);
    } catch (error) {
      console.error('Failed to load AI terms:', error);
      toast.error('Failed to load AI terms');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await aiTerms.update(hotelId, terms);
      toast.success('AI terms saved successfully!');
    } catch (error) {
      console.error('Failed to save AI terms:', error);
      toast.error('Failed to save AI terms');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (section, field, value) => {
    setTerms(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const addBlackoutDate = (date) => {
    if (!date) return;
    setTerms(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        blackout_dates: [...(prev.availability.blackout_dates || []), date]
      }
    }));
  };

  const removeBlackoutDate = (date) => {
    setTerms(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        blackout_dates: prev.availability.blackout_dates.filter(d => d !== date)
      }
    }));
  };

  if (loading) {
    return (
      <Card data-testid="ai-terms-config">
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="ai-terms-config" className="border-blue-500/20 bg-gradient-to-br from-blue-500/5 to-purple-500/5">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <Settings className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <CardTitle className="text-lg">AI Agent Terms & Policies</CardTitle>
              <CardDescription className="mt-1">
                Configure how AI assistants sell your rooms via chat and MCP
              </CardDescription>
            </div>
          </div>
          <Switch
            checked={terms?.enabled || false}
            onCheckedChange={(enabled) => setTerms({...terms, enabled})}
            data-testid="ai-terms-enabled-toggle"
          />
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {terms?.enabled && (
          <Tabs defaultValue="pricing" className="w-full">
            <TabsList className="grid w-full grid-cols-3 lg:grid-cols-6">
              <TabsTrigger value="pricing" className="text-xs">
                <DollarSign className="w-3 h-3 mr-1" />
                Pricing
              </TabsTrigger>
              <TabsTrigger value="availability" className="text-xs">
                <Calendar className="w-3 h-3 mr-1" />
                Availability
              </TabsTrigger>
              <TabsTrigger value="policies" className="text-xs">
                <Shield className="w-3 h-3 mr-1" />
                Policies
              </TabsTrigger>
              <TabsTrigger value="cancellation" className="text-xs">
                Cancel
              </TabsTrigger>
              <TabsTrigger value="requirements" className="text-xs">
                <UserCheck className="w-3 h-3 mr-1" />
                Guests
              </TabsTrigger>
              <TabsTrigger value="voice" className="text-xs">
                <Sparkles className="w-3 h-3 mr-1" />
                Voice
              </TabsTrigger>
            </TabsList>

            {/* Pricing Rules */}
            <TabsContent value="pricing" className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="min-price" className="text-xs">Min Price/Night ($)</Label>
                  <Input
                    id="min-price"
                    type="number"
                    placeholder="No minimum"
                    value={terms?.pricing?.min_price_per_night || ''}
                    onChange={(e) => updateField('pricing', 'min_price_per_night', parseFloat(e.target.value) || null)}
                    data-testid="ai-terms-min-price"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="max-price" className="text-xs">Max Price/Night ($)</Label>
                  <Input
                    id="max-price"
                    type="number"
                    placeholder="No maximum"
                    value={terms?.pricing?.max_price_per_night || ''}
                    onChange={(e) => updateField('pricing', 'max_price_per_night', parseFloat(e.target.value) || null)}
                    data-testid="ai-terms-max-price"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="mcp-markup" className="text-xs">MCP Channel Markup (%)</Label>
                <Input
                  id="mcp-markup"
                  type="number"
                  placeholder="0"
                  value={terms?.pricing?.mcp_markup_percent || 0}
                  onChange={(e) => updateField('pricing', 'mcp_markup_percent', parseFloat(e.target.value) || 0)}
                  data-testid="ai-terms-mcp-markup"
                />
                <p className="text-xs text-muted-foreground">Add % to MCP bookings (e.g., 5 = 5% commission)</p>
              </div>

              <div className="flex items-center justify-between p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg">
                <div>
                  <Label className="text-xs font-medium">Dynamic Pricing Tiers</Label>
                  <p className="text-xs text-muted-foreground">Allow Early Bird, Advance, Last Minute pricing</p>
                </div>
                <Switch
                  checked={terms?.pricing?.allow_dynamic_pricing ?? true}
                  onCheckedChange={(v) => updateField('pricing', 'allow_dynamic_pricing', v)}
                  data-testid="ai-terms-dynamic-pricing"
                />
              </div>

              <Separator />

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="promo-discount" className="text-xs">Promo Discount (%)</Label>
                  <Input
                    id="promo-discount"
                    type="number"
                    placeholder="0"
                    value={terms?.pricing?.promo_discount_percent || ''}
                    onChange={(e) => updateField('pricing', 'promo_discount_percent', parseFloat(e.target.value) || null)}
                    data-testid="ai-terms-promo-discount"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="promo-until" className="text-xs">Promo Valid Until</Label>
                  <Input
                    id="promo-until"
                    type="date"
                    value={terms?.pricing?.promo_valid_until || ''}
                    onChange={(e) => updateField('pricing', 'promo_valid_until', e.target.value)}
                    data-testid="ai-terms-promo-until"
                  />
                </div>
              </div>
            </TabsContent>

            {/* Availability Rules */}
            <TabsContent value="availability" className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="min-stay" className="text-xs">Min Stay (nights)</Label>
                  <Input
                    id="min-stay"
                    type="number"
                    min="1"
                    value={terms?.availability?.min_stay_nights || 1}
                    onChange={(e) => updateField('availability', 'min_stay_nights', parseInt(e.target.value) || 1)}
                    data-testid="ai-terms-min-stay"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="max-stay" className="text-xs">Max Stay (nights)</Label>
                  <Input
                    id="max-stay"
                    type="number"
                    placeholder="No limit"
                    value={terms?.availability?.max_stay_nights || ''}
                    onChange={(e) => updateField('availability', 'max_stay_nights', parseInt(e.target.value) || null)}
                    data-testid="ai-terms-max-stay"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="advance-days" className="text-xs">Min Advance Booking (days)</Label>
                  <Input
                    id="advance-days"
                    type="number"
                    min="0"
                    value={terms?.availability?.advance_booking_days || 0}
                    onChange={(e) => updateField('availability', 'advance_booking_days', parseInt(e.target.value) || 0)}
                    data-testid="ai-terms-advance-days"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="max-advance-days" className="text-xs">Max Advance Booking (days)</Label>
                  <Input
                    id="max-advance-days"
                    type="number"
                    value={terms?.availability?.max_advance_booking_days || 365}
                    onChange={(e) => updateField('availability', 'max_advance_booking_days', parseInt(e.target.value) || 365)}
                    data-testid="ai-terms-max-advance-days"
                  />
                </div>
              </div>

              <Separator />

              <div className="space-y-3">
                <Label className="text-xs font-medium">Blackout Dates</Label>
                <div className="flex gap-2">
                  <Input
                    type="date"
                    id="blackout-date-input"
                    className="flex-1"
                    data-testid="ai-terms-blackout-input"
                  />
                  <Button
                    onClick={() => {
                      const input = document.getElementById('blackout-date-input');
                      if (input.value) {
                        addBlackoutDate(input.value);
                        input.value = '';
                      }
                    }}
                    variant="outline"
                    size="sm"
                    data-testid="ai-terms-add-blackout"
                  >
                    Add
                  </Button>
                </div>
                {terms?.availability?.blackout_dates?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {terms.availability.blackout_dates.map((date, idx) => (
                      <Badge
                        key={idx}
                        variant="secondary"
                        className="cursor-pointer hover:bg-red-100 dark:hover:bg-red-900"
                        onClick={() => removeBlackoutDate(date)}
                      >
                        {date} ×
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Booking Policies */}
            <TabsContent value="policies" className="space-y-4 pt-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg">
                  <div>
                    <Label className="text-xs font-medium">Pay at Property</Label>
                    <p className="text-xs text-muted-foreground">Allow guests to pay on arrival</p>
                  </div>
                  <Switch
                    checked={terms?.booking_policies?.pay_at_property_enabled ?? true}
                    onCheckedChange={(v) => updateField('booking_policies', 'pay_at_property_enabled', v)}
                    data-testid="ai-terms-pay-at-property"
                  />
                </div>

                <div className="flex items-center justify-between p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg">
                  <div>
                    <Label className="text-xs font-medium">Crypto Payments</Label>
                    <p className="text-xs text-muted-foreground">Accept cryptocurrency payments</p>
                  </div>
                  <Switch
                    checked={terms?.booking_policies?.crypto_payment_enabled ?? true}
                    onCheckedChange={(v) => updateField('booking_policies', 'crypto_payment_enabled', v)}
                    data-testid="ai-terms-crypto-enabled"
                  />
                </div>

                <div className="flex items-center justify-between p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg">
                  <div>
                    <Label className="text-xs font-medium">Phone Confirmation Required</Label>
                    <p className="text-xs text-muted-foreground">For pay-at-property bookings</p>
                  </div>
                  <Switch
                    checked={terms?.booking_policies?.phone_confirmation_required ?? true}
                    onCheckedChange={(v) => updateField('booking_policies', 'phone_confirmation_required', v)}
                    data-testid="ai-terms-phone-confirm"
                  />
                </div>

                <div className="flex items-center justify-between p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg">
                  <div>
                    <Label className="text-xs font-medium">Auto-confirm Crypto Bookings</Label>
                    <p className="text-xs text-muted-foreground">Skip manual confirmation for crypto</p>
                  </div>
                  <Switch
                    checked={terms?.booking_policies?.auto_confirm_crypto ?? true}
                    onCheckedChange={(v) => updateField('booking_policies', 'auto_confirm_crypto', v)}
                    data-testid="ai-terms-auto-confirm"
                  />
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="deposit-percent" className="text-xs">Deposit Required (%)</Label>
                  <Input
                    id="deposit-percent"
                    type="number"
                    min="0"
                    max="100"
                    placeholder="0 = No deposit"
                    value={terms?.booking_policies?.deposit_percent || 0}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value) || 0;
                      updateField('booking_policies', 'deposit_percent', val);
                      updateField('booking_policies', 'deposit_required', val > 0);
                    }}
                    data-testid="ai-terms-deposit"
                  />
                </div>
              </div>
            </TabsContent>

            {/* Cancellation Policies */}
            <TabsContent value="cancellation" className="space-y-4 pt-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="free-cancel-hours" className="text-xs">Free Cancellation (hours before check-in)</Label>
                  <Input
                    id="free-cancel-hours"
                    type="number"
                    min="0"
                    value={terms?.cancellation?.free_cancellation_hours || 24}
                    onChange={(e) => updateField('cancellation', 'free_cancellation_hours', parseInt(e.target.value) || 24)}
                    data-testid="ai-terms-free-cancel"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="partial-cancel-hours" className="text-xs">Partial Refund Window (hours before)</Label>
                  <Input
                    id="partial-cancel-hours"
                    type="number"
                    min="0"
                    value={terms?.cancellation?.partial_refund_hours || 48}
                    onChange={(e) => updateField('cancellation', 'partial_refund_hours', parseInt(e.target.value) || 48)}
                    data-testid="ai-terms-partial-cancel-hours"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="partial-refund-pct" className="text-xs">Partial Refund Percentage</Label>
                  <Input
                    id="partial-refund-pct"
                    type="number"
                    min="0"
                    max="100"
                    value={terms?.cancellation?.partial_refund_percent || 50}
                    onChange={(e) => updateField('cancellation', 'partial_refund_percent', parseInt(e.target.value) || 50)}
                    data-testid="ai-terms-partial-refund-pct"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="no-show-refund" className="text-xs">No-Show Refund (%)</Label>
                  <Input
                    id="no-show-refund"
                    type="number"
                    min="0"
                    max="100"
                    value={terms?.cancellation?.no_show_refund_percent || 0}
                    onChange={(e) => updateField('cancellation', 'no_show_refund_percent', parseInt(e.target.value) || 0)}
                    data-testid="ai-terms-no-show-refund"
                  />
                </div>
              </div>
            </TabsContent>

            {/* Guest Requirements */}
            <TabsContent value="requirements" className="space-y-4 pt-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg">
                  <Label className="text-xs">Require Passport/ID Number</Label>
                  <Switch
                    checked={terms?.guest_requirements?.require_passport || false}
                    onCheckedChange={(v) => updateField('guest_requirements', 'require_passport', v)}
                    data-testid="ai-terms-require-passport"
                  />
                </div>

                <div className="flex items-center justify-between p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg">
                  <Label className="text-xs">Require Full Address</Label>
                  <Switch
                    checked={terms?.guest_requirements?.require_address || false}
                    onCheckedChange={(v) => updateField('guest_requirements', 'require_address', v)}
                    data-testid="ai-terms-require-address"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="min-age" className="text-xs">Minimum Guest Age</Label>
                  <Input
                    id="min-age"
                    type="number"
                    min="0"
                    value={terms?.guest_requirements?.min_age || 18}
                    onChange={(e) => updateField('guest_requirements', 'min_age', parseInt(e.target.value) || 18)}
                    data-testid="ai-terms-min-age"
                  />
                </div>
              </div>
            </TabsContent>

            {/* Brand Voice */}
            <TabsContent value="voice" className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label htmlFor="tone" className="text-xs">AI Tone</Label>
                <select
                  id="tone"
                  value={terms?.brand_voice?.tone || 'professional'}
                  onChange={(e) => updateField('brand_voice', 'tone', e.target.value)}
                  className="w-full p-2 border rounded-md text-sm"
                  data-testid="ai-terms-tone"
                >
                  <option value="professional">Professional</option>
                  <option value="friendly">Friendly</option>
                  <option value="luxury">Luxury</option>
                  <option value="casual">Casual</option>
                </select>
              </div>

              <div className="flex items-center justify-between p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg">
                <Label className="text-xs">Allow Upselling</Label>
                <Switch
                  checked={terms?.brand_voice?.upsell_allowed ?? true}
                  onCheckedChange={(v) => updateField('brand_voice', 'upsell_allowed', v)}
                  data-testid="ai-terms-upsell"
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg">
                <Label className="text-xs">Suggest Alternative Dates</Label>
                <Switch
                  checked={terms?.brand_voice?.flexible_on_dates ?? true}
                  onCheckedChange={(v) => updateField('brand_voice', 'flexible_on_dates', v)}
                  data-testid="ai-terms-flexible-dates"
                />
              </div>
            </TabsContent>
          </Tabs>
        )}

        {!terms?.enabled && (
          <Alert className="border-blue-500/20 bg-blue-500/5">
            <Info className="w-4 h-4 text-blue-600" />
            <AlertDescription className="text-xs text-blue-700 dark:text-blue-600">
              Enable AI Terms to customize how AI assistants present and book your rooms. When disabled, default policies apply.
            </AlertDescription>
          </Alert>
        )}

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full"
          data-testid="ai-terms-save"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save AI Terms'}
        </Button>

        {terms?.last_updated && (
          <p className="text-xs text-center text-muted-foreground">
            Last updated: {new Date(terms.last_updated).toLocaleString()}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
